import React from "react";
import {useSearchParams} from "react-router-dom";
import {Box, Stack, Tab, Tabs, Typography} from "@mui/material";
import {useRegistryProject} from "../RegistryProjectContext";
import {RegistryGeneralSettings} from "./RegistryGeneralSettings";
import {RegistrySubProjectSettings} from "./RegistrySubProjectSettings";
import {RegistryMembersSettings} from "./RegistryMembersSettings";
import {RegistryProtectionSettings} from "./RegistryProtectionSettings";

const TABS = [
  {value: "general", label: "General", Component: RegistryGeneralSettings},
  // "languages" is the tab's name from before sub projects, kept for old links
  {value: "sub-projects", label: "Sub Projects", Component: RegistrySubProjectSettings, aliases: ["languages"]},
  {value: "members", label: "Members", Component: RegistryMembersSettings},
  {value: "protection", label: "Protection", Component: RegistryProtectionSettings},
];

// Settings of a registry project: project information, sub projects, members and message protection
export const RegistrySettingsPage = () => {
  const {project} = useRegistryProject();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = TABS.find(tab => tab.value === searchParams.get("tab") || tab.aliases?.includes(searchParams.get("tab")))
    || TABS[0];
  const handleTabChange = (e, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("add");
      if (value === TABS[0].value) next.delete("tab"); else next.set("tab", value);
      return next;
    }, {replace: true});
  };

  return (
    <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: "md", width: "100%", mx: "auto", boxSizing: "border-box"}}>
      <Box>
        <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7", textTransform: "uppercase"}}>
          SETTINGS
        </Typography>
        <Typography component={"h1"} sx={{fontSize: {xs: 22, md: 26}, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em", mt: 0.5}}>
          Project Settings
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.5}}>
          Manage <strong>{project.name}</strong>'s information, sub projects, access and message protection.
        </Typography>
      </Box>

      <Box sx={{bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "18px", boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)"}}>
        <Box sx={{px: {xs: 2, md: 4}, pt: {xs: 1, md: 1.5}}}>
          <Tabs
            value={activeTab.value}
            onChange={handleTabChange}
            variant={"scrollable"}
            scrollButtons={false}
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {textTransform: "none", minHeight: 44, minWidth: "auto", fontWeight: 700, fontSize: 13, px: 0, mr: 3, color: "#8A8AA0", "&.Mui-selected": {color: "#8491F7"}},
              "& .MuiTabs-indicator": {backgroundColor: "#8491F7"},
            }}
          >
            {TABS.map(tab => <Tab key={tab.value} label={tab.label} value={tab.value}/>)}
          </Tabs>
        </Box>
        <activeTab.Component/>
      </Box>
    </Stack>
  );
};
